import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { subscribeAttendanceChanged } from "../../../hooks/useTodayAttendance.js";
import { toDayStatus, type AttendanceDay, type Range } from "./attendance-calendar.js";

// The API's own cell shape (GET /api/attendance/monthly-grid). `self=true` is
// documented in monthly-grid.schema.ts as "permitted for every role", which is
// what lets this widget ship unrestricted.
interface GridCell { status: string; note?: string }
interface GridResponse {
  days: string[];
  rows: { cells: GridCell[] }[];
}

const rangeKey = (r: Range) => `${r.from}:${r.to}`;

// Shared across every mounted widget, so the full and compact versions on the
// same dashboard fetch a given week once between them.
const cache = new Map<string, AttendanceDay[]>();

export interface AttendanceRangeState {
  /** null while loading. */
  days: AttendanceDay[] | null;
  error: boolean;
  reload: () => void;
}

/**
 * Loads one date range of the viewer's own attendance, cached by range so
 * stepping back and forth is instant, and reloaded whenever a check-in or
 * check-out happens anywhere in the app.
 */
export function useAttendanceRange(range: Range): AttendanceRangeState {
  const [days, setDays] = useState<AttendanceDay[] | null>(null);
  const [error, setError] = useState(false);
  const key = rangeKey(range);

  const load = useCallback(async (r: Range, force = false) => {
    const k = rangeKey(r);
    if (!force) {
      const hit = cache.get(k);
      if (hit) { setDays(hit); setError(false); return; }
    }
    setDays(null);
    setError(false);
    try {
      const { data } = await api.get<GridResponse>(
        `/attendance/monthly-grid?from=${r.from}&to=${r.to}&granularity=day&self=true&limit=1`,
      );
      const cells = data.rows[0]?.cells ?? [];
      const list: AttendanceDay[] = data.days.map((date, i) => {
        const cell = cells[i];
        const status = cell ? toDayStatus(cell.status) : "none";
        return cell?.note ? { date, status, note: cell.note } : { date, status };
      });
      cache.set(k, list);
      setDays(list);
    } catch (e) {
      logError("useAttendanceRange.load", e, { from: r.from, to: r.to });
      setError(true);
    }
  }, []);

  useEffect(() => { void load(range); }, [load, key]); // eslint-disable-line react-hooks/exhaustive-deps

  // A check-in must light the day up at once, so drop the cached period first.
  const latest = useRef(range);
  latest.current = range;
  useEffect(() => subscribeAttendanceChanged(() => {
    cache.delete(rangeKey(latest.current));
    void load(latest.current, true);
  }), [load]);

  const reload = useCallback(() => { void load(latest.current, true); }, [load]);

  return { days, error, reload };
}
